
function uuidv4() {
    return crypto.randomUUID(); // modern browsers
}

export function getOrSetAnonymousId() {
    const k = 'hv_anonymousId';
    let id = localStorage.getItem(k);
    if (!id) {
        id = uuidv4();
        localStorage.setItem(k, id);
    }
    return id;
}

function getOrSetSessionId() {
    const k = 'hv_sessionId';
    let id = sessionStorage.getItem(k);
    if (!id) {
        id = uuidv4();
        sessionStorage.setItem(k, id);
    }
    return id;
}

function getUserIdFromToken() {
    try {
        const token = localStorage.getItem('hv_jwt');
        if (!token) return null;

        // Simple manual decode of JWT payload (middle part)
        const parts = token.split('.');
        if (parts.length !== 3) return null;

        const payload = JSON.parse(atob(parts[1]));
        return payload.id || payload._id || null;
    } catch (e) {
        return null;
    }
}

function getUserEmailFromToken() {
    try {
        const token = localStorage.getItem('hv_jwt');
        if (!token) return null;

        // Simple manual decode of JWT payload (middle part)
        const parts = token.split('.');
        if (parts.length !== 3) return null;

        const payload = JSON.parse(atob(parts[1]));
        return payload.email || null;
    } catch (e) {
        return null;
    }
}

/** ExecEdge funnel saves formData under this key (see funnel.tsx). Funnel JWT only has leadId, not email. */
function getExecEdgeFormIdentity() {
    try {
        const raw = localStorage.getItem('execedge_funnel_progress');
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        const fd = parsed?.formData;
        if (!fd || typeof fd !== 'object') return null;
        const name = fd.name && String(fd.name).trim();
        const email = fd.email && String(fd.email).trim();
        if (name) {
            return { display: name, form_email: email || undefined };
        }
        if (email) {
            return { display: email };
        }
    } catch (e) { }
    return null;
}

/**
 * UTM from current URL. Trims keys/values so malformed links like
 * `?utm_source%20=%20hv` (spaces) still parse. Uses short keys: source, medium, …
 */
function getUTMFromURL() {
    const params = new URLSearchParams(window.location.search);
    const out = {};
    for (const [rawKey, rawVal] of params.entries()) {
        const key = String(rawKey).trim().toLowerCase();
        const val = rawVal == null ? '' : String(rawVal).trim();
        if (!key || !val) continue;
        if (key === 'utm_source') out.source = val;
        else if (key === 'utm_medium') out.medium = val;
        else if (key === 'utm_campaign') out.campaign = val;
        else if (key === 'utm_term') out.term = val;
        else if (key === 'utm_content') out.content = val;
    }
    return out;
}

/**
 * Prefer fresh UTMs from the URL (last-touch), merge onto any prior localStorage,
 * then persist. This must NOT return stale storage when the URL has new params.
 */
function getStoredUTM() {
    const k = 'hv_utm';
    const fresh = getUTMFromURL();

    let existing = {};
    try {
        const raw = localStorage.getItem(k);
        if (raw) existing = JSON.parse(raw);
        if (existing && typeof existing !== 'object') existing = {};
    } catch (e) {
        existing = {};
    }

    if (Object.keys(fresh).length > 0) {
        const merged = { ...existing, ...fresh };
        localStorage.setItem(k, JSON.stringify(merged));
        return merged;
    }

    if (Object.keys(existing).length > 0) return existing;
    return null;
}

/**
 * @typedef {Object} TrackPayload
 * @property {string} eventName
 * @property {string|null} [userId]
 * @property {string|null} [email]
 * @property {string} [anonymousId]
 * @property {string} [sessionId]
 * @property {string} [pageUrl]
 * @property {string|null} [referrer]
 * @property {string} [source]
 * @property {Object} [eventProperties]
 * @property {Object} [utm]
 * @property {string} [occurredAt]
 */

const lastEvent = {
    key: '',
    time: 0
};

/**
 * Tracks an event
 * @param {Partial<TrackPayload> & { eventName: string; pageUrl?: string }} payload
 */
export async function trackEvent(arg1, arg2) {
    let payload = {};
    if (typeof arg1 === 'string') {
        payload = {
            eventName: arg1,
            eventProperties: arg2?.eventProperties || arg2,
            ...arg2
        };
    } else {
        payload = arg1;
    }
    // Simple deduplication for React StrictMode double-invocation
    const now = Date.now();
    // Create a deterministic key for the event (ignoring timestamp)
    const eventKey = JSON.stringify({
        eventName: payload.eventName,
        pageUrl: payload.pageUrl ?? window.location.href
    });

    if (eventKey === lastEvent.key && (now - lastEvent.time < 500)) {
        return;
    }

    lastEvent.key = eventKey;
    lastEvent.time = now;

    // Determine Identity: HV JWT email -> ExecEdge funnel form -> hireveda_application_data -> null
    const tokenEmail = getUserEmailFromToken();

    let formIdentity = null;
    let extraProperties = {};

    if (!tokenEmail) {
        const execEdge = getExecEdgeFormIdentity();
        if (execEdge) {
            formIdentity = execEdge.display;
            if (execEdge.form_email) extraProperties.form_email = execEdge.form_email;
        }
    }

    if (!tokenEmail && !formIdentity) {
        // Try to get form data
        try {
            const data = localStorage.getItem('hireveda_application_data');
            if (data) {
                const parsed = JSON.parse(data);
                const name = parsed.name && parsed.name.trim();
                const email = parsed.email && parsed.email.trim();

                // If Name is present, use it as the main identity!
                if (name) {
                    formIdentity = name;
                    // AND save the email in the background so the dashboard can find it later
                    if (email) extraProperties.form_email = email;
                } else if (email) {
                    formIdentity = email;
                }
            }
        } catch (e) { }
    }

    const finalProperties = {
        ...(payload.eventProperties || {}),
        ...extraProperties
    };

    const body = {
        eventName: payload.eventName,
        userId: payload.userId ?? getUserIdFromToken() ?? null,
        email: payload.email ?? tokenEmail ?? formIdentity ?? null,
        anonymousId: getOrSetAnonymousId(),
        sessionId: getOrSetSessionId(),
        pageUrl: payload.pageUrl ?? window.location.href,
        referrer: payload.referrer ?? (document.referrer || null),
        source: payload.source ?? 'web',
        eventProperties: finalProperties,
        utm: payload.utm ?? getStoredUTM(),
        occurredAt: payload.occurredAt ?? new Date().toISOString(),
    };

    try {
        const isLocal = window.location.hostname === 'localhost';
        const endpoint = isLocal ? 'http://localhost:4002/api/track' : '/api/track';

        // Use navigator.sendBeacon for better reliability on unload, falling back to fetch
        // Note: sendBeacon sends POST requests
        // However, sendBeacon doesn't support JSON content-type by default easily without Blob, 
        // and some backends might be strict. We'll stick to fetch with keepalive for now as per user snippet.

        await fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
            keepalive: true, // helpful for page unload events
        });
    } catch (err) {
        // swallow errors quietly (do not block UX)
        console.warn('trackEvent failed', err);
    }
}